import { OnboardingBaseTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { i18next } from '@frontend/i18n';
import { HOTSPOT_TARGET, type OnboardingFlow } from '@frontend/product-onboarding';
import { BaseProductTaskContent } from './components/base-product-task-content';
import { GetStartedContent } from './components/get-started-content';
import { HotspotTooltipContent } from './components/hotspot-tooltip-content';
import { CI_BASE_ONBOARDING, CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS } from './constant';
import { CI_BASE_ONBOARDING_EVENTS, CI_BASE_ONBOARDING_TRACKING_IDS } from './tracking';

interface BuildCIBaseOnboardingFlowArgs {
  canAssignUserRoles: boolean;
}

export function buildCIBaseOnboardingFlow({ canAssignUserRoles }: BuildCIBaseOnboardingFlowArgs): OnboardingFlow {
  const tasks: OnboardingFlow['tasks'] = [
    {
      id: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      label: i18next.t('Uncover enhanced Call Insights', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Quickly see the call summary and transcript', { ns: 'call-intel-onboarding' }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.viewCallInsights,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewCallInsights,
      },
    },
    {
      id: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      label: i18next.t('Access call summaries in the patient profile', { ns: 'call-intel-onboarding' }),
      description: i18next.t('View previous conversations in the patient Overview and History', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.viewPatientContext,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.viewPatientContext,
      },
    },
  ];

  const hotspots: OnboardingFlow['hotspots'] = [
    // Explore Call History
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALLS_NAVIGATION_MENU,
      target: HOTSPOT_TARGET.CALLS_NAVIGATION_MENU,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.callsNavigation,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.callsNavigation,
      },
    },
    // View Call Insights: open the panel, see summary & transcript
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_HISTORY_CALL_INSIGHT_BUTTON,
      target: HOTSPOT_TARGET.CALL_HISTORY_CALL_INSIGHT_BUTTON,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.callInsightButton,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.callInsightButton,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      target: HOTSPOT_TARGET.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
      group: OnboardingBaseTask.BASE_TASK_VIEW_CALL_INSIGHTS,
      completes: [
        CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_HISTORY_CALL_INSIGHT_BUTTON,
        CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALLS_NAVIGATION_MENU,
      ],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.transcriptTab,
      },
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.transcriptTab,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.transcriptTab,
      },
    },

    // View Patient Context: call intelligence inside the patient profile
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR,
      target: HOTSPOT_TARGET.PATIENT_CONTACT_AVATAR,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.patientAvatar,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.patientAvatar,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_LAST_CALL_CARD,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      completes: [CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_CONTACT_AVATAR],
      completeGroups: [OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT],
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.lastCallCard,
      },
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.lastCallCard,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.lastCallCard,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_LAST_CALL_CARD,
      tracking: {
        seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.viewHistoryButton,
        interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.viewHistoryButton,
      },
    },
    {
      key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_HISTORY_TAB_CALL,
      target: HOTSPOT_TARGET.PATIENT_PROFILE_HISTORY_TAB_CALL,
      group: OnboardingBaseTask.BASE_TASK_VIEW_PATIENT_CONTEXT,
      kind: 'info',
      dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
      tooltip: {
        Content: HotspotTooltipContent,
        trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.patientHistoryCall,
      },
    },
  ];

  if (canAssignUserRoles) {
    tasks.push({
      id: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
      label: i18next.t('Loop in your team', { ns: 'call-intel-onboarding' }),
      description: i18next.t('Assign the Data Analyst role to your team members in User Management', {
        ns: 'call-intel-onboarding',
      }),
      tracking: {
        completedEvent: CI_BASE_ONBOARDING_EVENTS.taskCompleted.assignUserRoles,
        navigationTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.taskNavigationClick.assignUserRoles,
      },
    });

    hotspots.push(
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        target: HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        dependencies: {
          all: [
            CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.CALL_INSIGHT_PANEL_TRANSCRIPT_TAB,
            CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.PATIENT_PROFILE_VIEW_HISTORY_BUTTON,
          ],
        },
        tracking: {
          seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.settingsIcon,
          interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.settingsIcon,
        },
      },
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        target: HOTSPOT_TARGET.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.TOP_BAR_SETTINGS_ICON,
        tracking: {
          seenEvent: CI_BASE_ONBOARDING_EVENTS.hotspotSeen.userManagementNav,
          interactionTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.hotspotInteraction.userManagementNav,
        },
      },
      {
        key: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        target: HOTSPOT_TARGET.ORGANIZATION_SETTINGS_USER_TABLE_ACTION_BUTTON,
        group: OnboardingBaseTask.BASE_TASK_ASSIGN_USER_ROLES,
        kind: 'info',
        dependencies: CALL_INTEL_BASE_ONBOARDING_HOTSPOT_KEYS.SETTINGS_PAGE_USER_MANAGEMENT_GROUP,
        tooltip: {
          Content: HotspotTooltipContent,
          trackingSeenEvent: CI_BASE_ONBOARDING_EVENTS.tooltipSeen.userTableAction,
        },
      }
    );
  }

  return {
    id: CI_BASE_ONBOARDING.id,
    priority: CI_BASE_ONBOARDING.priority,
    NavContent: GetStartedContent,
    TaskContent: BaseProductTaskContent,
    tasks,
    hotspots,
    tracking: {
      guideTriggerTrackingId: CI_BASE_ONBOARDING_TRACKING_IDS.getStartedNavClick,
      checklistSeenEvent: CI_BASE_ONBOARDING_EVENTS.checklistSeen,
    },
  };
}
